import React, { useState, useEffect } from "react";
import { Table, Button, Space, Modal } from "antd"; // Import Table, Button, Space, Modal from Ant Design
import { EngagementApi } from "../../services/EngagementApi";
import { useLocation } from "react-router-dom";
import { useAxios } from "../../contexts/AxiosProvider";
import { useSnackbar } from "notistack";

const { Column } = Table;

// DateConverter function
export const DateConverter = (dateString) => {
  const d = new Date(dateString);
  return d.toDateString();
};

export default function EngagementTable() {
  const axios = useAxios();
  const [engagementData, setEngagementData] = useState({
    engagementList: [],
    totalEngagements: 0,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState(null);

  useEffect(() => {
    fetchAllEngagements();
  }, [page]);

  const fetchAllEngagements = async () => {
    try {
      setLoading(true);
      const data = {
        user_id: location.state.userId,
        search: false,
        searchParam: "",
        pageNumber: page,
        pageSize: rowsPerPage,
        status: "pending",
      };
      const response = await EngagementApi.getAllEngagements(axios, data);
      if (response.status === 200) {
        setEngagementData(response.data);
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (pageNumber) => {
    setPage(pageNumber);
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      const data = {
        engagement_id: selectedEngagement.engagement_id,
        status: "approved",
      };
      const response = await EngagementApi.engagementStatusChanged(axios, data);
      if (response.status === 200) {
        enqueueSnackbar("Pipeline approved successfully !!", {
          variant: "success",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        if (engagementData.engagementList.length === 1 && page > 1) {
          setPage((prevPage) => prevPage - 1);
        }
        fetchAllEngagements();
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setLoading(false);
      setApproveModalVisible(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      const data = {
        engagement_id: selectedEngagement.engagement_id,
        status: "rejected",
      };
      const response = await EngagementApi.engagementStatusChanged(axios, data);
      if (response.status === 200) {
        enqueueSnackbar("Pipeline rejected successfully !!", {
          variant: "success",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        if (engagementData.engagementList.length === 1 && page > 1) {
          setPage((prevPage) => prevPage - 1);
        }
        fetchAllEngagements();
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar("500 Internal Server Error!", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      }
    } finally {
      setLoading(false);
      setRejectModalVisible(false);
    }
  };

  const handleApproveModal = (record) => {
    setSelectedEngagement(record);
    setApproveModalVisible(true);
  };

  const handleRejectModal = (record) => {
    setSelectedEngagement(record);
    setRejectModalVisible(true);
  };

  return (
    <>
      <Table
        dataSource={engagementData.engagementList}
        pagination={{
          total: engagementData.totalEngagements,
          pageSize: rowsPerPage,
          current: page,
          onChange: handleChangePage,
        }}
      >
        <Column title="Sub-Function Name" dataIndex="client_name" key="client_name" />
        <Column
          title="Pipeline Name"
          dataIndex="engagement_name"
          key="engagement_name"
        />
        <Column title="Created By" dataIndex="created_by" key="created_by" />
        <Column
          title="Created On"
          dataIndex="created_at"
          key="created_at"
          render={(created_at) => DateConverter(created_at)}
        />
        <Column
          title="Action"
          key="action"
          render={(text, record) => (
            <Space size="middle">
              <Button type="primary" onClick={() => handleApproveModal(record)}>
                Approve
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => handleRejectModal(record)}
              >
                Reject
              </Button>
            </Space>
          )}
        />
      </Table>

      {/* Approve Modal */}
      <Modal
        title="Approve Pipeline"
        visible={approveModalVisible}
        onOk={handleApprove}
        onCancel={() => setApproveModalVisible(false)}
      >
        <p>
          Are you sure you want to approve the Pipeline "
          {selectedEngagement?.engagement_name}"?
        </p>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Pipeline"
        visible={rejectModalVisible}
        onOk={handleReject}
        onCancel={() => setRejectModalVisible(false)}
      >
        <p>
          Are you sure you want to reject the Pipeline "
          {selectedEngagement?.engagement_name}"?
        </p>
      </Modal>
    </>
  );
}
